import { Drawer } from "antd";
import classNames from "classnames";
import cuid from "cuid";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { ATTACHMENT_TYPES, RELATIONSHIP } from "utils/constants";
import { downloadDocument } from "utils/helpers/files";
import validation from "./validation";
import BeneficiaryChips from "./widgets/BeneficiaryChips";
import BeneficiaryForm from "./widgets/BeneficiaryForm";
import ProductCard from "./widgets/ProductCard";

import { MdOutlineDiversity1 } from "react-icons/md";
import { useMediaQuery } from "react-responsive";
import "swiper/css";
import "swiper/css/grid";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import Button from "../Components/Button";
import Footer from "../Components/Footer";
import Header from "../Components/Header";
import Swiper from "../Components/Swiper";

const View = ({
  onSubmit,
  products,
  disabledProducts,
  contact,
  refetchCosts,
  downloadSingleDocument,
  locked,
  duplicateProject,
  onBack,
  costsLoading,
}) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const TRANSLATION = useMemo(() => t("DETAIL", { returnObjects: true }), [t]);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const initialValues = useMemo(() => {
    const relatedPersons = get(contact, "relatedPersons", []);
    return {
      spouse: relatedPersons.find(
        ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
      ),
      children: relatedPersons.filter(
        ({ relationship }) => relationship === RELATIONSHIP.CHILD,
      ),
    };
  }, [contact]);

  const onChangeRelatedPersons = useCallback(
    async ({ spouse, children }) => {
      await refetchCosts({
        contact: {
          id: contact.id,
          relatedPersons: [spouse, ...children].filter(Boolean),
        },
      });
      setAddBeneficiaries((prev) => ({ ...prev, visible: false }));
    },
    [contact.id, refetchCosts],
  );

  const { handleSubmit, values, ...formProps } = useFormik({
    initialValues,
    onSubmit: onChangeRelatedPersons,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
  });

  const initialChildrenLength = useMemo(
    () => get(values, "children", []).length,
    [values],
  );
  const [childrenNumber, setChildrenNumber] = useState(initialChildrenLength);

  const hasBeneficiaries = useMemo(
    () => !isEmpty(get(values, "spouse")) || !isEmpty(get(values, "children")),
    [values],
  );

  const [addBeneficiaries, setAddBeneficiaries] = useState({
    visible: false,
    spouse: !isEmpty(get(values, "spouse")),
    children: !isEmpty(get(values, "children")),
  });
  const [disabled, setDisabled] = useState({});

  const beneficiarySummary = useMemo(() => {
    if (!addBeneficiaries.spouse && childrenNumber === 0) return "";
    if (addBeneficiaries.spouse && childrenNumber > 0) {
      return `Conjoint et ${childrenNumber} enfant${
        childrenNumber > 1 ? "s" : ""
      }`;
    }
    if (addBeneficiaries.spouse) return "Conjoint";
    if (childrenNumber > 0) {
      return `${childrenNumber} enfant${childrenNumber > 1 ? "s" : ""}`;
    }
    return "";
  }, [addBeneficiaries.spouse, childrenNumber]);

  useEffect(() => {
    const children = get(values, "children", []);
    const currentLength = children.length;
    const targetLength = childrenNumber;

    if (currentLength < targetLength) {
      const newChildren = Array.from(
        { length: targetLength - currentLength },
        () => ({
          id: cuid(),
          relationship: RELATIONSHIP.CHILD,
          identity: {
            id: cuid(),
            user: {
              id: cuid(),
              birthDate: null,
            },
          },
        }),
      );
      formProps.setFieldValue("children", [...children, ...newChildren]);
    } else if (currentLength > targetLength) {
      formProps.setFieldValue("children", children.slice(0, targetLength));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenNumber]);

  useEffect(() => {
    if (prevRef.current && nextRef.current) {
      prevRef.current.classList.add("custom-prev");
      nextRef.current.classList.add("custom-next");
    }
  }, []);

  const handleYesClick = useCallback(() => {
    setAddBeneficiaries((prev) => ({
      ...prev,
      visible: true,
    }));
  }, []);

  const handleNoClick = useCallback(() => {
    if (!hasBeneficiaries) return;

    formProps.setFieldValue("spouse", undefined);
    formProps.setFieldValue("children", []);
    setChildrenNumber(0);
    setAddBeneficiaries({
      spouse: false,
      children: false,
      visible: false,
    });
    handleSubmit();
  }, [hasBeneficiaries, formProps, handleSubmit]);

  return (
    <div className="solyon">
      <div className="solyon__container">
        <div className="solyon__wrapper">
          <Header
            title="Vous y êtes ! Voici nos offres spécialement conçues pour vous"
            highlight="nos offres"
            subtitle="Offres"
            // onBack={onBack}
          />
          <div className="solyon__body">
            {/* <div className="related__persons">
            <div className="--head">
              <div className="--left">
                <MdOutlineDiversity1 size={24} />
                <span className="question">
                  {TRANSLATION.PRODUCTS.ADD_BENEFICIARY}
                </span>
              </div>

              <div className="--actions">
                <Button
                  size="small"
                  type={classNames({
                    primary: addBeneficiaries?.status,
                    "gray-light": !addBeneficiaries?.status,
                  })}
                  onClick={handleYesClick}
                >
                  {TRANSLATION.YES}
                </Button>

                <Button
                  size="small"
                  type={classNames({
                    primary: !addBeneficiaries?.status,
                    "gray-light": addBeneficiaries?.status,
                  })}
                  onClick={handleNoClick}
                >
                  {TRANSLATION.NO}
                </Button>
              </div>
            </div>

            <div
              className={classNames("--forms", {
                hidden: !addBeneficiaries.visible,
              })}
            >
              <div className="beneficiary_container--expand--row">
                <Input
                  type="checkbox"
                  label={TRANSLATION.PRODUCTS.SPOUSE}
                  checked={addBeneficiaries.spouse}
                  onChange={({ target: { checked } }) => {
                    setAddBeneficiaries((prev) => ({
                      ...prev,
                      spouse: checked,
                    }));
                    if (checked)
                      formProps.setFieldValue("spouse", {
                        id: cuid(),
                        relationship: RELATIONSHIP.SPOUSE,
                        identity: {
                          id: cuid(),
                          user: {
                            id: cuid(),
                            birthDate: null,
                          },
                        },
                      });
                    else formProps.setFieldValue("spouse", undefined);
                  }}
                />
                <Input
                  width={261}
                  type="date"
                  label={TRANSLATION.BIRTH_DATE.LABEL}
                  placeholder={"JJ / MM / AAAA"}
                  format={DATE_FORMAT}
                  disabled={!addBeneficiaries.spouse}
                  showToday={false}
                  {...bindDateInputProps({
                    name: "spouse.identity.user.birthDate",
                    values,
                    ...formProps,
                  })}
                  defaultPickerValue={
                    !get(values, `spouse.identity.user.birthDate`) &&
                    dayjs().subtract(24, "years")
                  }
                  disabledDate={(current) =>
                    current > isMajor || current < hundredYearsAgoStart
                  }
                />
              </div>
              <div className="divider" />
              <div className="beneficiary_container--expand--row">
                <Input
                  type="checkbox"
                  label={TRANSLATION.PRODUCTS.CHILDREN}
                  checked={addBeneficiaries.children}
                  onChange={({ target: { checked } }) => {
                    if (!checked) {
                      setChildrenNumber(0);
                      formProps.setFieldValue("children", []);
                    } else if (childrenNumber === 0) setChildrenNumber(1);
                    setAddBeneficiaries((prev) => ({
                      ...prev,
                      children: checked,
                    }));
                  }}
                />
                <div className="children-count">
                  <Button
                    type={"primary"}
                    className="children-count__button"
                    onClick={() => setChildrenNumber((prev) => +prev - 1)}
                    disabled={
                      childrenNumber < 2 || !addBeneficiaries.children
                    }
                  >
                    <FaMinus size={14} />
                  </Button>
                  <div className="flex--grow-1">
                    <Input
                      type="number"
                      min={0}
                      max={12}
                      placeholder={"0"}
                      name={"gender"}
                      value={childrenNumber}
                      onChange={({ target: { value } }) => {
                        setChildrenNumber(+value);
                      }}
                      disabled={!addBeneficiaries.children}
                    />
                  </div>
                  <Button
                    type={"primary"}
                    className="children-count__button"
                    onClick={() => setChildrenNumber((prev) => +prev + 1)}
                    disabled={
                      childrenNumber > 11 || !addBeneficiaries.children
                    }
                  >
                    <FaPlus size={14} />
                  </Button>
                </div>
              </div>
              <div className="children-birthday">
                {Array(childrenNumber)
                  .fill()
                  .map((_, i) => (
                    <div
                      key={`child-${i}`}
                      className="children-birthday--row"
                    >
                      <Label
                        label={`${TRANSLATION.NUMBERS_MAPPING[i + 1] || i + 1
                          } ${TRANSLATION.CHILDREN_BIRTHDATES.CHILD}`}
                      />
                      <Input
                        width={261}
                        label={TRANSLATION.BIRTH_DATE.LABEL}
                        placeholder={"JJ / MM / AAAA"}
                        type="date"
                        variant="simple"
                        format={DATE_FORMAT}
                        disabledDate={(current) => {
                          return (
                            current > dayjs() ||
                            current < twentySevenYearsAgoStart
                          );
                        }}
                        defaultPickerValue={
                          !get(
                            values,
                            `children.${i}.identity.user.birthDate`
                          ) && dayjs().subtract(1, "years")
                        }
                        showToday={false}
                        {...bindDateInputProps({
                          name: `children.${i}.identity.user.birthDate`,
                          values,
                          ...formProps,
                        })}
                      />
                    </div>
                  ))}
              </div>
              <Button
                block
                type="primary"
                onClick={() => handleSubmit()}
                disabled={
                  !isEmpty(formProps.errors) ||
                  (childrenNumber === 0 && !addBeneficiaries.spouse)
                }
              >
                {TRANSLATION.PRODUCTS.SUBMIT}
              </Button>

            </div>

          </div> */}
            <div className="related_persons">
              <div className="beneficiary_container">
                <div
                  className="beneficiary_container--main"
                  {...(isMobile && {
                    onClick: () => {
                      if (isMobile) setDrawerVisible(true);
                      else
                        setAddBeneficiaries((prev) => ({
                          ...prev,
                          visible: !prev.visible,
                        }));
                    },
                  })}
                >
                  <div className="beneficiary_container--main--left">
                    <MdOutlineDiversity1 size={24} />

                    {isMobile ? (
                      <span className="question">
                        {addBeneficiaries.spouse || childrenNumber > 0
                          ? TRANSLATION.PRODUCTS.YOUR_BENEFICIARIES
                          : TRANSLATION.PRODUCTS.ADD_BENEFICIARY}
                      </span>
                    ) : (
                      <span className="question">
                        {TRANSLATION.PRODUCTS.ADD_BENEFICIARY}
                      </span>
                    )}
                  </div>

                  {isMobile && beneficiarySummary && (
                    <span className="selected--beneficiaries">
                      {beneficiarySummary}
                    </span>
                  )}

                  <div className="--action">
                    <Button
                      size="small"
                      type={classNames({
                        primary: hasBeneficiaries,
                        "gray-light": !hasBeneficiaries,
                      })}
                      onClick={handleYesClick}
                    >
                      {TRANSLATION.YES}
                    </Button>

                    <Button
                      size="small"
                      type={classNames({
                        primary: !hasBeneficiaries,
                        "gray-light": hasBeneficiaries,
                      })}
                      onClick={handleNoClick}
                    >
                      {TRANSLATION.NO}
                    </Button>
                  </div>
                </div>
                {!isMobile && (
                  <div
                    className={classNames("beneficiary_container--expand", {
                      hidden: !addBeneficiaries.visible,
                    })}
                  >
                    <BeneficiaryForm
                      values={values}
                      formProps={formProps}
                      addBeneficiaries={addBeneficiaries}
                      setAddBeneficiaries={setAddBeneficiaries}
                      childrenNumber={childrenNumber}
                      setChildrenNumber={setChildrenNumber}
                      translation={TRANSLATION}
                      onSubmit={handleSubmit}
                    />
                  </div>
                )}
              </div>
              {!isMobile && (
                <BeneficiaryChips
                  values={values}
                  formProps={formProps}
                  addBeneficiaries={addBeneficiaries}
                  setAddBeneficiaries={setAddBeneficiaries}
                  childrenNumber={childrenNumber}
                  setChildrenNumber={setChildrenNumber}
                  translation={TRANSLATION}
                  onSubmit={handleSubmit}
                />
              )}
            </div>
            <div className="solyon-products">
              <Swiper>
                {products.map(({ id, cost, currency, insurancePlan }, i) => {
                  const isDisabled = disabledProducts.includes(
                    insurancePlan.id,
                  );
                  const isActionDisabled =
                    !isEmpty(disabled) ||
                    addBeneficiaries.visible ||
                    costsLoading;
                  const flagshipGuarantees = get(
                    insurancePlan,
                    "fields.flagshipGuarantees",
                    [],
                  );

                  return (
                    <ProductCard
                      key={id}
                      price={{
                        cost,
                        currency,
                        period: TRANSLATION.PRODUCTS.PER_MONTH,
                      }}
                      name={get(insurancePlan, "name")}
                      icon={get(insurancePlan, "icon")}
                      action={{
                        onClick: () => {
                          setDisabled((prev) => ({ ...prev, [id]: true }));
                          onSubmit(insurancePlan.id);
                        },
                        label: TRANSLATION.PRODUCTS.CHOOSE_OFFER,
                        loading: disabled[id] || costsLoading,
                        disabled: isActionDisabled,
                      }}
                      disabled={isDisabled}
                      info={{
                        title: "Les garanties phares",
                        items: flagshipGuarantees,
                      }}
                      link={{
                        onClick: () => {
                          downloadSingleDocument({
                            variables: {
                              where: {
                                insurancePlans: { id: insurancePlan?.id },
                                type: ATTACHMENT_TYPES.BROCHURE,
                              },
                            },
                            onCompleted: (_) => {
                              const { document } = omitDeep(
                                _.downloadSingleDocument,
                                "__typename",
                              );
                              downloadDocument(document);
                            },
                          });
                        },
                        label: TRANSLATION.PRODUCTS.COVERAGE_DETAIL,
                      }}
                      recommendation={{
                        label: "Recommandé",
                        value: i === 1,
                      }}
                    />
                  );
                })}
              </Swiper>
              <div className="swiper__arrows">
                <div className="swiper-button-prev"></div>
                <div className="swiper-button-next"></div>
              </div>
            </div>
          </div>
          <Footer />

          <Drawer
            title={TRANSLATION.PRODUCTS.ADD_BENEFICIARY}
            placement="bottom"
            className="solyon__drawer"
            closable
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            height="85vh"
          >
            <BeneficiaryChips
              values={values}
              formProps={formProps}
              addBeneficiaries={addBeneficiaries}
              setAddBeneficiaries={setAddBeneficiaries}
              childrenNumber={childrenNumber}
              setChildrenNumber={setChildrenNumber}
              translation={TRANSLATION}
              onSubmit={handleSubmit}
            />
            <div className="beneficiary_container">
              <div className="beneficiary_container--expand">
                <BeneficiaryForm
                  values={values}
                  formProps={formProps}
                  addBeneficiaries={addBeneficiaries}
                  setAddBeneficiaries={setAddBeneficiaries}
                  childrenNumber={childrenNumber}
                  setChildrenNumber={setChildrenNumber}
                  translation={TRANSLATION}
                  onSubmit={() => {
                    handleSubmit();
                    setDrawerVisible(false);
                  }}
                />
              </div>
            </div>
          </Drawer>
        </div>
      </div>
    </div>
  );
};

export default View;
